/**
 * The interactive primitives the console needs and did not have.
 *
 * `primitives.tsx` shipped five components for Phase 2's screens and says in its header why it is
 * not fifteen: "components invented before their screens are components built to the wrong shape".
 * These ten have their screens now — the admin console, the invite dialog, the workspace switcher,
 * the settings page — so they are built against them rather than against a catalogue.
 *
 * ## Hand-rolled, and the reason is not taste
 *
 * `ux.md` reaches for Radix in two places. This repository has no Radix and no shadcn, and adding
 * one is a new runtime dependency in the bundle every dashboard visit downloads — against budgets
 * (`infra/verify-bundle-budget.mjs`) that are already the tripwire ADR 0008 §7 asks them to be. So
 * these are written out, and what a headless library would have given for free is written out with
 * them: a focus trap that returns focus, Escape that closes, arrow keys that move a roving
 * tabindex, `aria-*` that names the state rather than describing the paint.
 *
 * Every one styles through SEMANTIC tokens. Tailwind's stock palette is cleared in theme.css so
 * `bg-blue-600` does not compile; a MISSPELT semantic name compiles to nothing at all, which is
 * what `infra/verify-design-tokens.mjs` exists to catch.
 *
 * Tier: MVP.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { Icon } from './icons'
import { HintTip } from './primitives'

// -------------------------------------------------------------------------------------------
// Switch

export type SwitchProps = {
  readonly checked: boolean
  readonly onChange: (next: boolean) => void
  /** The accessible name. Rendered visually by the caller's own row, so it is not drawn here. */
  readonly label: string
  readonly disabled?: boolean
  /** Shown to a screen reader and as a title, for a control that is off for a stated reason. */
  readonly disabledReason?: string
}

/**
 * `role="switch"`, which is not the same as a checkbox and is not interchangeable with one.
 *
 * A checkbox means "include this"; a switch means "this is on now". Every row on the workspace
 * settings page is the second, and a screen reader that announced "checkbox, checked" for "Require
 * hand-drawn signatures" would describe a form rather than a state.
 *
 * The glyph inside the knob is SignNow's and it earns its place: a pill with no mark is
 * distinguishable only by position and colour, and position is what a reader with low vision is
 * least able to judge against a track they cannot see the ends of.
 */
export function Switch({ checked, onChange, label, disabled, disabledReason }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      // BOTH, matching the menu item further down this file, which has always spread the pair.
      // `title` is a mouse-hover tooltip and nothing else: it never reaches a screen reader on a
      // disabled control, and it never reaches a touch device at all. On Email preferences the
      // locked reason is the ENTIRE answer to "why can't I turn this off?" for four of six rows,
      // so a reason only a hovering mouse can see is a reason most readers do not have.
      {...(disabled === true && disabledReason !== undefined
        ? { title: disabledReason, 'aria-description': disabledReason }
        : {})}
      disabled={disabled === true}
      onClick={() => onChange(!checked)}
      className={`ease-standard inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-accent border-accent' : 'bg-surface-sunken border-border-strong'
      }`}
    >
      <span
        aria-hidden="true"
        className={`ease-standard flex size-5 items-center justify-center rounded-full transition-transform duration-150 ${
          checked ? 'bg-surface-raised translate-x-5' : 'bg-surface-raised translate-x-0.5'
        }`}
      >
        <Icon
          name={checked ? 'check' : 'x'}
          size={12}
          className={checked ? 'text-accent' : 'text-text-muted'}
        />
      </span>
    </button>
  )
}

/**
 * A switch WITH its own row: the name and the sentence on the left, the toggle on the right.
 *
 * `Switch` draws the pill and nothing else — its `label` is the accessible name and its doc says
 * the caller draws the visible row. Three callers did not: the workspace settings `Control`, the
 * email preferences row and, before this, the two hand-rolled rows in Reminders each stacked a
 * grey help line UNDER a bare pill, so a sighted reader saw a toggle with no name, a sentence
 * below it, and a hand's width of empty space to the right of the toggle. That is the founder's
 * "put the data line next to the toggle button, it looks awkward" (2026-09-13), and the fix is one
 * row component rather than the same flexbox written out in four places and drifting in three.
 *
 * The layout is Reminders' — text left, toggle right — because that is the one settings layout
 * already shipped here, it is what every settings page worth copying does, and it is the only
 * arrangement where a long sentence does not push the toggle off the reader's line of sight.
 *
 * `items-start`, not `items-center`: a stored-only note runs to three lines on a narrow column,
 * and centring against the whole block would leave the toggle floating beside the middle of a
 * paragraph rather than beside the name of the thing it switches.
 */
export type SwitchRowProps = {
  readonly checked: boolean
  readonly onChange: (next: boolean) => void
  /** The name of the setting. Also the switch's accessible name. */
  readonly label: string
  /** One sentence saying what it does. */
  readonly help?: ReactNode
  /** A second, quieter line — "saved and nothing reads it yet", and the like. */
  readonly note?: ReactNode
  readonly disabled?: boolean
  /** Shown to a screen reader and as a title, for a control that is off for a stated reason. */
  readonly disabledReason?: string
}

export function SwitchRow({
  checked,
  onChange,
  label,
  help,
  note,
  disabled,
  disabledReason,
}: SwitchRowProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-text-primary text-sm font-medium">{label}</span>
        {help === undefined || help === null || help === '' ? null : (
          <span className="text-text-muted text-sm">{help}</span>
        )}
        {note === undefined || note === null || note === '' ? null : (
          <span className="text-text-muted text-xs italic">{note}</span>
        )}
      </div>
      {/* `pt-0.5` lands the 24px pill on the 20px line-height of the name beside it. */}
      <div className="shrink-0 pt-0.5">
        <Switch
          checked={checked}
          onChange={onChange}
          label={label}
          disabled={disabled === true}
          {...(disabledReason === undefined ? {} : { disabledReason })}
        />
      </div>
    </div>
  )
}

// -------------------------------------------------------------------------------------------
// Dialog

export type DialogProps = {
  readonly open: boolean
  readonly onClose: () => void
  readonly title: string
  /** One clause under the title. */
  readonly description?: ReactNode
  readonly children: ReactNode
  /** The buttons. Rendered in a footer that is sticky on a short screen. */
  readonly footer?: ReactNode
  /** `xl` is for a document: wide enough for a landscape page at 100% with a little air. */
  readonly size?: 'sm' | 'md' | 'lg' | 'xl'
}

const DIALOG_WIDTH: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-6xl',
}

/**
 * A modal with a real focus trap.
 *
 * Three obligations, all of which a `<div role="dialog">` leaves to the author and all of which get
 * forgotten: focus moves IN when it opens, Tab cannot leave while it is open, and focus returns to
 * whatever opened it when it closes. The third is the one nobody notices is missing until they use
 * a keyboard — without it, closing a dialog drops focus onto `<body>` and the next Tab starts from
 * the top of the page.
 *
 * The backdrop closes on click, and the panel stops propagation so a click inside does not. Escape
 * closes from anywhere inside, which is why the listener is on the panel rather than on the window:
 * two open dialogs would otherwise both close on one press.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: DialogProps) {
  const panel = useRef<HTMLDivElement | null>(null)
  const body = useRef<HTMLDivElement | null>(null)
  const restoreTo = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const descriptionId = useId()

  useEffect(() => {
    if (!open) return
    restoreTo.current = document.activeElement as HTMLElement | null
    // The first focusable thing in the BODY — not in the panel, which would be the Close button in
    // the header. Landing on Close is technically "focus moved in" and is the wrong place: a reader
    // opening "Create Workspace" should be on the name field, and one who wanted to close it would
    // have pressed Escape. The panel itself is the fallback, because a dialog with no controls at
    // all is still a thing a reader has to be taken to.
    const first =
      body.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      panel.current?.querySelector<HTMLElement>(FOCUSABLE) ??
      panel.current
    first?.focus()
    return () => {
      restoreTo.current?.focus?.()
    }
  }, [open])

  if (!open) return null

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onClose()
      return
    }
    if (event.key !== 'Tab') return
    const node = panel.current
    if (node === null) return
    const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    )
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (first === undefined || last === undefined) return
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      className="bg-scrim fixed inset-0 z-50 flex items-start justify-center overflow-y-auto px-4 py-[8vh]"
      onClick={onClose}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(description === undefined ? {} : { 'aria-describedby': descriptionId })}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
        className={`border-border-strong bg-surface-raised w-full ${DIALOG_WIDTH[size]} rounded-xl border shadow-xl outline-none`}
      >
        <div className="border-border-subtle flex items-start justify-between gap-4 border-b px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-text-primary m-0 text-base font-semibold">
              {title}
            </h2>
            {description === undefined ? null : (
              <p id={descriptionId} className="text-text-muted m-0 mt-1 text-sm">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-text-muted hover:bg-surface-sunken hover:text-text-primary flex size-8 shrink-0 items-center justify-center rounded-md"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
        <div ref={body} className="px-5 py-4">
          {children}
        </div>
        {footer === undefined ? null : (
          <div className="border-border-subtle flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

// -------------------------------------------------------------------------------------------
// Tabs

export type TabsProps = {
  readonly tabs: ReadonlyArray<{ readonly id: string; readonly label: ReactNode }>
  readonly active: string
  readonly onChange: (id: string) => void
  readonly label: string
}

/**
 * A roving tabindex, which is what `role="tablist"` actually means.
 *
 * Only the selected tab is in the tab order; Left/Right move between them and Home/End jump to the
 * ends. Rendering every tab as `tabIndex={0}` is the common mistake and it makes a five-tab strip
 * five stops on the way to the content.
 */
export function Tabs({ tabs, active, onChange, label }: TabsProps) {
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((t) => t.id === active)
    if (index < 0) return
    let next = index
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = tabs.length - 1
    else return
    event.preventDefault()
    const target = tabs[next]
    if (target !== undefined) onChange(target.id)
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="border-border-subtle flex gap-1 border-b"
    >
      {tabs.map((tab) => {
        const selected = tab.id === active
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              selected
                ? 'border-accent text-accent'
                : 'text-text-muted hover:text-text-primary border-transparent'
            }`}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

// -------------------------------------------------------------------------------------------
// Menu

export type MenuItem = {
  readonly id: string
  readonly label: ReactNode
  readonly onSelect?: () => void
  readonly disabled?: boolean
  /**
   * Why it is disabled, shown as a title and announced.
   *
   * `ux.md` §12's dead-end ban is the reason this is not optional in practice: a greyed item with
   * no explanation is a control that silently does nothing, which is the thing the ban is about.
   */
  readonly disabledReason?: string
  readonly tone?: 'default' | 'danger'
}

export type MenuProps = {
  readonly label: string
  readonly items: readonly MenuItem[]
  /** The trigger's contents. Defaults to a ⋯ glyph. */
  readonly trigger?: ReactNode
  readonly align?: 'start' | 'end'
  /**
   * A visible heading above the items, for a menu whose options do not name their own subject.
   *
   * `label` is the accessible name and a sighted reader never sees it, which is fine for a menu of
   * verbs ("Remove", "Duplicate") and not fine for one of NOUNS. The assurance menu is three bare
   * words — Standard, Verified, Digital — behind a ⋯ glyph, and the testing team reported exactly
   * that: no way to tell what is being chosen. Optional, so every existing menu is unchanged.
   */
  readonly heading?: string
}

/** Where the open panel sits, in viewport coordinates. */
type MenuPosition = {
  readonly top: number
  readonly left: number
}

/** The gap between the trigger and the panel — what `mt-1` used to draw. */
const MENU_TRIGGER_GAP = 4
/** How close to a viewport edge the panel may come before it is flipped or clamped. */
const MENU_VIEWPORT_MARGIN = 8

/**
 * A button plus a menu, with arrow keys, Escape, and a click-away that closes.
 *
 * ## The panel is `fixed`, and that is the whole point
 *
 * It was `absolute`, and every row-actions menu in the console was unusable because of it. Each of
 * this app's tables wraps its `<table>` in `overflow-x-auto` so narrow viewports scroll sideways
 * instead of crushing the columns — and CSS says that when one axis is not `visible`, the other
 * computes from `visible` to `auto` too. So that wrapper is a scroll box on BOTH axes, and an
 * absolutely positioned panel inside it is clipped by it: on a one-row table the wrapper is ~69px
 * tall, the panel is ~237px, and the 168px that do not fit go behind a scrollbar that appears
 * around the table. The reported symptom is a page that "does not fit, with no option to resize"
 * — a menu trapped in a 69px window. `z-index` does not help; nothing escapes an ancestor's clip
 * by painting order.
 *
 * `fixed` takes the panel out of that flow entirely: it is positioned against the viewport, so no
 * ancestor scroll box clips it, and it stops contributing height to the wrapper, which removes the
 * phantom scrollbar as well. The cost is that the coordinates have to be computed rather than
 * declared, which is what `place()` does — below the trigger, flipped above when the viewport
 * bottom is nearer than the panel is tall, and clamped so a menu on the last column never opens
 * off-screen.
 *
 * No portal, so no `react-dom` dependency in this package and no change to the DOM tree: the panel
 * stays inside `root`, which is what keeps the click-away and the keyboard handler working as they
 * did. The one thing `fixed` cannot survive is an ancestor with a `transform`, `filter` or
 * `will-change` — those make a new containing block for fixed descendants. No ancestor of a table
 * in `Shell.tsx` has one; if that ever changes, this becomes a portal.
 */
export function Menu({ label, items, trigger, align = 'end', heading }: MenuProps) {
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(0)
  const [position, setPosition] = useState<MenuPosition | null>(null)
  const root = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const close = useCallback(() => setOpen(false), [])

  const place = useCallback(() => {
    const anchor = triggerRef.current
    const panel = panelRef.current
    if (anchor === null || panel === null) return

    const rect = anchor.getBoundingClientRect()
    const width = panel.offsetWidth
    const height = panel.offsetHeight

    // Below by default; above only when below would run off the bottom AND above actually fits.
    const below = rect.bottom + MENU_TRIGGER_GAP
    const above = rect.top - MENU_TRIGGER_GAP - height
    const flip = below + height + MENU_VIEWPORT_MARGIN > window.innerHeight
    const top = flip && above >= MENU_VIEWPORT_MARGIN ? above : below

    // `end` hangs the panel's right edge off the trigger's, `start` its left — then clamp, so the
    // last column's menu slides inboard rather than off the screen.
    const preferred = align === 'end' ? rect.right - width : rect.left
    const rightmost = window.innerWidth - width - MENU_VIEWPORT_MARGIN
    const left = Math.max(MENU_VIEWPORT_MARGIN, Math.min(preferred, rightmost))

    setPosition({ top, left })
  }, [align])

  // Layout effect, not effect: the panel must be measured and placed before the browser paints,
  // or it shows for a frame at the top-left corner.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    place()
  }, [open, place, items])

  // Viewport coordinates go stale the moment anything scrolls. Capture phase, because the scroll
  // that moves the trigger is usually an ancestor's, not the window's.
  useEffect(() => {
    if (!open) return
    const replace = () => place()
    window.addEventListener('scroll', replace, true)
    window.addEventListener('resize', replace)
    return () => {
      window.removeEventListener('scroll', replace, true)
      window.removeEventListener('resize', replace)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return
    const onDocClick = (event: MouseEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) close()
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open, close])

  const enabled = items.filter((i) => i.disabled !== true)

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      close()
      return
    }
    if (!open) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((c) => (c + 1) % Math.max(1, enabled.length))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((c) => (c - 1 + Math.max(1, enabled.length)) % Math.max(1, enabled.length))
    } else if (event.key === 'Enter' || event.key === ' ') {
      const item = enabled[cursor]
      if (item !== undefined) {
        event.preventDefault()
        item.onSelect?.()
        close()
      }
    }
  }

  return (
    <div ref={root} className="relative inline-block" onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => {
          setCursor(0)
          setOpen((o) => !o)
        }}
        className="text-text-muted hover:bg-surface-sunken hover:text-text-primary flex size-8 items-center justify-center rounded-md"
      >
        {trigger ?? <Icon name="more-horizontal" size={16} />}
      </button>
      {open ? (
        <div
          ref={panelRef}
          role="menu"
          aria-label={label}
          // Inline, because these are measured rather than declared. `opacity` covers the frame
          // before the layout effect runs; it is not `visibility: hidden`, which would take the
          // panel out of the accessibility tree and hide it from a `getByRole` query too.
          style={{
            position: 'fixed',
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            opacity: position === null ? 0 : 1,
          }}
          className="border-border-strong bg-surface-raised z-30 min-w-52 rounded-lg border p-1 shadow-xl"
        >
          {heading === undefined ? null : (
            <p className="text-text-muted px-2.5 pb-1 pt-1.5 text-xs font-medium uppercase tracking-wide">
              {heading}
            </p>
          )}
          {items.map((item) => {
            const disabled = item.disabled === true
            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                disabled={disabled}
                {...(disabled && item.disabledReason !== undefined
                  ? { title: item.disabledReason, 'aria-description': item.disabledReason }
                  : {})}
                onClick={() => {
                  item.onSelect?.()
                  close()
                }}
                className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                  item.tone === 'danger'
                    ? 'text-danger hover:bg-danger/10'
                    : 'text-text-primary hover:bg-surface-sunken'
                }`}
              >
                {item.label}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

// -------------------------------------------------------------------------------------------
// Select, Checkbox

export type SelectProps = {
  readonly label: string
  readonly value: string
  readonly onChange: (next: string) => void
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>
  readonly disabled?: boolean
  /** `horizontal` puts the label beside the control, which is the console's standing layout. */
  readonly layout?: 'stacked' | 'horizontal'
  readonly hint?: ReactNode
}

export function Select({
  label,
  value,
  onChange,
  options,
  disabled,
  layout = 'horizontal',
  hint,
}: SelectProps) {
  const id = useId()
  const control = (
    <select
      id={id}
      value={value}
      disabled={disabled === true}
      // Names the hint whether it is drawn as a paragraph (stacked) or folded behind the glyph
      // (horizontal): the horizontal copy is visually hidden, not absent, so this still resolves.
      aria-describedby={hint === undefined ? undefined : `${id}-hint`}
      onChange={(e) => onChange(e.currentTarget.value)}
      className="h-control border-border-strong bg-field-bg text-text-primary w-full rounded-sm border px-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
  if (layout === 'horizontal') {
    return (
      // The hint is a tooltip beside the label, as on the horizontal `Field` (`HintTip` in
      // primitives.tsx says why): a settings page with a paragraph under one control and a glyph
      // beside the next would be two answers to one question on one screen.
      <div className="grid items-center gap-x-4 gap-y-1 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
        <span className="inline-flex items-center gap-1.5">
          <label htmlFor={id} className="text-text-primary text-sm font-medium">
            {label}
          </label>
          {hint === undefined ? null : (
            <HintTip label={label} hintId={`${id}-hint`}>
              {hint}
            </HintTip>
          )}
        </span>
        <div className="flex flex-col gap-1">{control}</div>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-text-primary text-sm font-medium">
        {label}
      </label>
      {control}
      {hint === undefined ? null : (
        <p id={`${id}-hint`} className="text-text-muted m-0 text-xs">
          {hint}
        </p>
      )}
    </div>
  )
}

export type CheckboxProps = {
  readonly checked: boolean
  readonly onChange: (next: boolean) => void
  readonly label: ReactNode
  readonly disabled?: boolean
}

export function Checkbox({ checked, onChange, label, disabled }: CheckboxProps) {
  const id = useId()
  return (
    <label
      htmlFor={id}
      className="text-text-primary flex cursor-pointer items-center gap-2 text-sm has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled === true}
        onChange={(e) => onChange(e.currentTarget.checked)}
        className="accent-accent size-4"
      />
      <span>{label}</span>
    </label>
  )
}

// -------------------------------------------------------------------------------------------
// Avatar, Chip

/**
 * Up to two initials from a name or, failing that, an email's local part.
 *
 * Moved here from `Shell.tsx`, where it was `initialsOf(email)` and could only ever read an
 * address. Every surface that shows a person now has a display name available — the member
 * directory returns one (20260912000900) — and an avatar that says "AL" for Ada Lovelace is worth
 * more than one that says "AD" because her address happens to start that way.
 */
export function initialsFrom(name: string | null, email: string | null): string {
  const source = name?.trim() ?? ''
  if (source.length > 0) {
    const parts = source.split(/\s+/).filter((p) => p.length > 0)
    const letters = parts
      .slice(0, 2)
      .map((p) => p[0] ?? '')
      .join('')
    if (letters.length > 0) return letters.toUpperCase()
  }
  if (email === null) return '?'
  const local = email.split('@')[0] ?? ''
  const parts = local.split(/[._+-]+/).filter((p) => p.length > 0)
  const letters = parts
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
  return letters.length > 0 ? letters.toUpperCase() : '?'
}

export type AvatarProps = {
  readonly name?: string | null
  readonly email?: string | null
  readonly size?: 'sm' | 'md' | 'lg'
  /**
   * A picture to show instead of initials: `/v1/profile/photo`, a colleague's photo endpoint, or
   * the `avatar_url` somebody stored.
   *
   * Optional, and staying optional is the point — every caller that has no picture keeps working
   * unchanged and keeps rendering initials, which is the honest state for anyone who never sets
   * one.
   */
  readonly src?: string | null
}

const AVATAR_SIZE: Record<NonNullable<AvatarProps['size']>, string> = {
  sm: 'size-6 text-[10px]',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
}

/**
 * A person, as a picture when there is one and as initials when there is not.
 *
 * ## This used to take no `src` at all
 *
 * `user_profiles.avatar_url` has been stored, validated and returned by the API since
 * `20260901017800`, and the settings page has had a field for it — and nothing ever rendered it.
 * Somebody could type their photo's URL in, save it, read it back, and never once see the picture.
 * That is the dead end `ux.md` §12 bans, with the whole round trip built and only the last inch
 * missing. This is the last inch.
 *
 * ## The fallback is on the ERROR, not only on the absence
 *
 * `src` can be a URL on somebody else's server that has since rotted, a photo endpoint answering
 * 404 because the account has none, or a network that is simply down. All three land on `onError`,
 * and all three should show initials rather than a broken-image glyph beside a person's name — so
 * the initials are rendered UNDERNEATH and the image sits on top, and a failed load reveals what
 * was always there. No spinner and no layout shift: the circle is the same size either way.
 *
 * `aria-hidden` on the whole thing, unchanged. An avatar is decoration beside a name that is
 * already written next to it, and announcing "image" or reading initials aloud would make a screen
 * reader say the person's name twice.
 */
export function Avatar({ name = null, email = null, size = 'md', src = null }: AvatarProps) {
  const [failed, setFailed] = useState(false)
  // A changed `src` is a different picture and deserves a fresh attempt — without this, one 404
  // would make every later photo for this slot render as initials for the life of the component.
  const [shown, setShown] = useState(src)
  if (shown !== src) {
    setShown(src)
    setFailed(false)
  }

  return (
    <span
      aria-hidden="true"
      className={`bg-accent/15 text-accent relative flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold ${AVATAR_SIZE[size]}`}
    >
      {initialsFrom(name, email)}
      {src !== null && src !== '' && !failed ? (
        <img
          src={src}
          alt=""
          className="absolute inset-0 size-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : null}
    </span>
  )
}

export type ChipProps = {
  readonly children: ReactNode
  readonly onRemove?: () => void
  readonly tone?: 'neutral' | 'danger'
  readonly removeLabel?: string
}

/** A removable token — an email in the invite dialog, a team on a member row. */
export function Chip({ children, onRemove, tone = 'neutral', removeLabel }: ChipProps) {
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-xs ${
        tone === 'danger' ? 'bg-danger/12 text-danger' : 'bg-surface-sunken text-text-muted'
      }`}
    >
      <span className="truncate">{children}</span>
      {onRemove === undefined ? null : (
        <button
          type="button"
          aria-label={removeLabel ?? 'Remove'}
          onClick={onRemove}
          className="hover:text-text-primary shrink-0"
        >
          <Icon name="x" size={12} />
        </button>
      )}
    </span>
  )
}

// -------------------------------------------------------------------------------------------
// Stepper

export type StepperStep = {
  readonly id: string
  readonly label: string
  readonly state: 'done' | 'current' | 'upcoming'
}

export type StepperProps = {
  readonly steps: readonly StepperStep[]
  readonly onSelect?: (id: string) => void
  readonly label?: string
}

/**
 * The composer's and the profile wizard's progress.
 *
 * An ordered list rather than a row of divs, because that is what it is: a screen reader announces
 * "3 items" and each step's own state, where a row of styled spans announces nothing. `aria-current`
 * marks the one being worked on.
 */
export function Stepper({ steps, onSelect, label = 'Progress' }: StepperProps) {
  return (
    <ol aria-label={label} className="m-0 flex list-none flex-wrap items-center gap-2 p-0">
      {steps.map((step, index) => {
        const clickable = onSelect !== undefined && step.state !== 'upcoming'
        const body = (
          <>
            <span
              aria-hidden="true"
              className={`flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                step.state === 'done'
                  ? 'bg-accent text-text-on-accent'
                  : step.state === 'current'
                    ? 'bg-accent/15 text-accent border-accent border'
                    : 'bg-surface-sunken text-text-muted'
              }`}
            >
              {step.state === 'done' ? <Icon name="check" size={12} /> : index + 1}
            </span>
            <span
              className={
                step.state === 'upcoming' ? 'text-text-muted text-sm' : 'text-text-primary text-sm'
              }
            >
              {step.label}
            </span>
          </>
        )
        return (
          <li key={step.id} className="flex items-center gap-2">
            {clickable ? (
              <button
                type="button"
                onClick={() => onSelect(step.id)}
                {...(step.state === 'current' ? { 'aria-current': 'step' as const } : {})}
                className="hover:bg-surface-sunken flex items-center gap-2 rounded-md px-1.5 py-1"
              >
                {body}
              </button>
            ) : (
              <span
                {...(step.state === 'current' ? { 'aria-current': 'step' as const } : {})}
                className="flex items-center gap-2 px-1.5 py-1"
              >
                {body}
              </span>
            )}
            {index < steps.length - 1 ? (
              <span aria-hidden="true" className="bg-border-subtle h-px w-6" />
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

// -------------------------------------------------------------------------------------------
// Toast

export type ToastTone = 'info' | 'success' | 'danger'

export type ToastProps = {
  readonly message: ReactNode
  readonly tone?: ToastTone
  readonly onDismiss: () => void
  /** Milliseconds before it dismisses itself. 0 keeps it until dismissed. */
  readonly timeout?: number
}

const TOAST_TONE: Record<ToastTone, string> = {
  info: 'border-border-strong',
  success: 'border-success',
  danger: 'border-danger',
}

/**
 * One transient message, bottom-right.
 *
 * `role="status"` with `aria-live="polite"` rather than `role="alert"`: a saved-setting confirmation
 * must not interrupt what somebody is typing. The danger tone is still polite, because by the time
 * a toast appears the failure has already happened and the control has already rolled back — the
 * urgent version of this belongs inline, next to the thing that failed.
 */
export function Toast({ message, tone = 'info', onDismiss, timeout = 4000 }: ToastProps) {
  useEffect(() => {
    if (timeout <= 0) return
    const handle = window.setTimeout(onDismiss, timeout)
    return () => window.clearTimeout(handle)
  }, [timeout, onDismiss])

  return (
    <div
      role="status"
      aria-live="polite"
      className={`bg-surface-raised text-text-primary fixed bottom-4 right-4 z-50 flex max-w-sm items-start gap-3 rounded-lg border-l-2 p-3 text-sm shadow-xl ${TOAST_TONE[tone]}`}
    >
      <span className="min-w-0 flex-1">{message}</span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="text-text-muted hover:text-text-primary shrink-0"
      >
        <Icon name="x" size={14} />
      </button>
    </div>
  )
}

// -------------------------------------------------------------------------------------------
// CopyButton

export type CopyButtonProps = {
  /** The exact text to place on the clipboard. Nothing is trimmed, wrapped or re-encoded. */
  readonly value: string
  /** The accessible name, when "Copy" is not specific enough beside several of these. */
  readonly label?: string
  /** How long "Copied" stands before the label returns, in milliseconds. */
  readonly timeout?: number
}

/**
 * Copy one short value, and say honestly whether it worked.
 *
 * Five screens had already written this inline — the API key panel, the invitation link, the
 * connector callback URL, the SAML metadata fields — and all five wrote the same three lines with
 * the same shape: `navigator.clipboard?.writeText(x).then(ok).catch(fail)`. This is that, once,
 * with the two things the inline copies each half-did.
 *
 * ## The clipboard is not always there, and pretending otherwise is the bug
 *
 * `navigator.clipboard` is undefined on an insecure origin and its write is permission-gated
 * everywhere else, so `writeText` is a promise that genuinely rejects in front of real people. A
 * button that swallowed that would leave someone holding nothing while believing they held the
 * value — which is worse than no button, because they will paste and not check.
 *
 * So a failure says **"Select and copy"**: it names what to do instead, and the value beside this
 * control is always selectable text. That is the rule this component cannot enforce by itself and
 * every caller owes: **the button is never the only door.** The Developer screen's comment put it
 * first and it is repeated here because it is the part that keeps the failure recoverable.
 *
 * ## The status is announced, not merely repainted
 *
 * The outcome lives in an `aria-live="polite"` region rather than in the button's own label. A
 * label that changes under a screen reader's cursor is announced as a new button, if at all;
 * "Copied" in a live region is announced as what happened. The visible text changes too, because a
 * sighted reader is watching the button and not the region.
 *
 * That means the word is on the page TWICE, on purpose, and it has one consequence worth knowing
 * before you write a test: `getByText('Copied')` matches both and fails as ambiguous. Query the
 * button by its role and name — which is the thing a person actually perceives — and read the
 * region with `getByRole('status')`.
 */
export function CopyButton({ value, label = 'Copy', timeout = 2000 }: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'unavailable'>('idle')

  useEffect(() => {
    if (state !== 'copied') return
    const t = setTimeout(() => setState('idle'), timeout)
    return () => clearTimeout(t)
  }, [state, timeout])

  const copy = useCallback(() => {
    // Optional chaining AND a try/catch: the property is missing on an insecure origin, and where
    // it exists the call itself can throw synchronously in a sandboxed frame rather than returning
    // a promise to reject.
    try {
      const clipboard = navigator.clipboard
      if (!clipboard) {
        setState('unavailable')
        return
      }
      clipboard.writeText(value).then(
        () => setState('copied'),
        () => setState('unavailable'),
      )
    } catch {
      setState('unavailable')
    }
  }, [value])

  const text = state === 'copied' ? 'Copied' : state === 'unavailable' ? 'Select and copy' : label

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={copy}
        className="text-text-muted border-border-subtle bg-surface-raised hover:border-border-strong hover:text-text-primary focus-visible:outline-accent inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <Icon name={state === 'copied' ? 'check' : 'copy'} size={12} />
        {text}
      </button>
      {/* The region is always present and always empty until something happens: a live region
          inserted at the moment of the announcement is frequently not announced at all. */}
      <span role="status" aria-live="polite" className="visually-hidden">
        {state === 'copied'
          ? 'Copied'
          : state === 'unavailable'
            ? 'Copying is unavailable here — select the value and copy it'
            : ''}
      </span>
    </span>
  )
}
